import { KeycloakRPTSingleton } from "../../../components/keycloak-provider";
import { Money } from "../../../gen/google/type/money_pb";
import { LoyaltyTierAPIPromiseClient } from "../../../gen/witbooking/backoffice/loyalty/tier/v1/loyalty_tier_api_grpc_web_pb";
import {
  GetLoyaltyTiersByBrandRequest,
  GetLoyaltyTiersByBrandResponse,
} from "../../../gen/witbooking/backoffice/loyalty/tier/v1/loyalty_tier_api_pb";
import { CustomerAPIPromiseClient } from "../../../gen/witbooking/loyalty/customer/v1/customer_api_grpc_web_pb";
import {
  CalculateCustomerTierRequest,
  CalculateCustomerTierResponse,
  Customer,
  GetCustomerRequest,
  GetCustomerResponse,
  GetCustomerTierInfoRequest,
  GetCustomerTierInfoResponse,
  UpgradeCustomerTierRequest,
  UpgradeCustomerTierResponse,
} from "../../../gen/witbooking/loyalty/customer/v1/customer_api_pb";
import { ReportAPIPromiseClient } from "../../../gen/witbooking/loyalty/report/v1/report_api_grpc_web_pb";
import {
  GetCustomerTransactionReportRequest,
  GetCustomerTransactionReportResponse,
} from "../../../gen/witbooking/loyalty/report/v1/report_api_pb";
import {
  TierConditionAmount,
  TierConditionLevel,
  TierConditionType,
} from "../../../gen/witbooking/loyalty/shared/tier_pb";
import { buildGrpcClient } from "../../../lib/grpc";
import { IHttpClient } from "../../../lib/http";
import { TierLevelUpgradeTransactionAmount } from "../../loyaltyHistorical";

export interface CustomerTransactionReportExportProps {
  brandTicker: string;
  brandId: string;
  filters: Record<string, string>;
}

export interface GetCustomerTransactionReportProps {
  pageSize: number;
  currentPage: number;
  brandId: string;
  filters: Record<string, string>;
}

export type GetCustomerTierInfo = GetCustomerTierInfoResponse.AsObject;
export type CalculateCustomerTierLevel = CalculateCustomerTierResponse.AsObject;
export type CustomerTierInfo = Omit<GetCustomerTierInfo, "levelsList">;
export type TierConditionTypeInfo = TierConditionType;
export type MoneyAsObject = Money.AsObject;
export type GetCustomer = Customer.AsObject;
export type UpgradedCustomerTier = UpgradeCustomerTierResponse.AsObject;
export type TierLevel = TierConditionLevel.AsObject;

const tierClient: LoyaltyTierAPIPromiseClient = buildGrpcClient(LoyaltyTierAPIPromiseClient);
const reportClient: ReportAPIPromiseClient = buildGrpcClient(ReportAPIPromiseClient);
const customerClient: CustomerAPIPromiseClient = buildGrpcClient(CustomerAPIPromiseClient);

class CustomerNotFoundError extends Error {
  constructor() {
    super();
    Object.setPrototypeOf(this, CustomerNotFoundError.prototype);
  }
}

export async function getLoyaltyTiersByBrand(
  brandTicker: string
): Promise<GetLoyaltyTiersByBrandResponse.AsObject> {
  const request = new GetLoyaltyTiersByBrandRequest();
  request.setBrandTicker(brandTicker);

  const metadata = {
    Authorization: `Bearer ${KeycloakRPTSingleton.rpt}`,
  };

  const response: GetLoyaltyTiersByBrandResponse = await tierClient.getLoyaltyTiersByBrand(
    request,
    metadata
  );
  return response.toObject();
}

export async function getCustomerTransactionReport(
  props: GetCustomerTransactionReportProps
): Promise<GetCustomerTransactionReportResponse.AsObject> {
  const request = new GetCustomerTransactionReportRequest();
  request.setPageSize(props.pageSize);
  request.setPageNumber(props.currentPage);
  request.setBrandId(props.brandId);
  Object.entries(props.filters)
    .filter(filter => filter)
    .forEach(([key, value]) => request.getFiltersMap().set(key, value as string));

  const metadata = {
    Authorization: `Bearer ${KeycloakRPTSingleton.rpt}`,
  };

  const response = await reportClient.getCustomerTransactionReport(request, metadata);
  return response.toObject();
}

// The report exports go through the backoffice-manager proxy (`useBackofficeClient()`): customer-rewards
// only serves the brand's report, even masked, once the proxy has checked the property against the brand.
export async function customerTransactionReportExport(
  backofficeClient: IHttpClient,
  props: CustomerTransactionReportExportProps
): Promise<Blob> {
  return backofficeClient.post(
    `/v1/loyalty/proxy/${props.brandTicker}/report/customer-transaction-report`,
    {
      brandIdentifier: props.brandId,
      reportFormat: "XLSX",
      filters: props.filters,
    },
    {
      responseType: "blob",
    }
  );
}

export async function customerTransactionReportFullExport(
  backofficeClient: IHttpClient,
  props: CustomerTransactionReportExportProps
): Promise<Blob> {
  return backofficeClient.post(
    `/v1/loyalty/proxy/${props.brandTicker}/report/customer-transaction-report/export`,
    {
      brandIdentifier: props.brandId,
      reportFormat: "XLSX",
      filters: props.filters,
    },
    {
      responseType: "blob",
    }
  );
}

export async function getCustomerTierInfo(customerId: string): Promise<GetCustomerTierInfo> {
  const request = new GetCustomerTierInfoRequest();
  request.setId(customerId);
  try {
    const response: GetCustomerTierInfoResponse = await customerClient.getCustomerTierInfo(request);
    return response.toObject();
  } catch (e) {
    console.error(`Error on getting tier information from customer [${customerId}]`, e);
    throw new Error("Unexpected reply obtaining customer tier information");
  }
}

export async function calculateCustomerTier(
  customerId: string,
  tierConditionType: TierConditionType,
  amount: TierLevelUpgradeTransactionAmount
): Promise<CalculateCustomerTierLevel> {
  const money = amountAsMoney(amount);
  const tierConditionAmount = new TierConditionAmount();
  tierConditionAmount.setTransactionsNumber(amount.transactionNumber);
  tierConditionAmount.setSpentAmount(money);

  const request = new CalculateCustomerTierRequest();
  request.setId(customerId);
  request.setTierConditionType(tierConditionType);
  request.setAmount(tierConditionAmount);
  try {
    const response: CalculateCustomerTierResponse = await customerClient.calculateCustomerTier(
      request
    );
    return response.toObject();
  } catch (e) {
    console.error(`Error on calculating tier level from customer [${customerId}]`, e);
    throw new Error("Unexpected reply calculating customer tier level");
  }
}

export async function getCustomer(customerId: string): Promise<GetCustomer> {
  const request = new GetCustomerRequest();
  request.setId(customerId);
  try {
    const response: GetCustomerResponse = await customerClient.getCustomer(request);
    if (!response.hasCustomer()) {
      throw new CustomerNotFoundError();
    }
    return response.getCustomer()!.toObject();
  } catch (e) {
    console.error(`Error on getting customer [${customerId}]`, e);
    if (e instanceof CustomerNotFoundError) {
      throw e;
    }
    throw new Error("Unexpected reply obtaining customer");
  }
}

export async function upgradeCustomerTierLevel(
  customerId: string,
  tierConditionType: TierConditionType,
  amount: TierLevelUpgradeTransactionAmount
): Promise<UpgradedCustomerTier> {
  const money = amountAsMoney(amount);
  const tierConditionAmount = new TierConditionAmount();
  tierConditionAmount.setTransactionsNumber(amount.transactionNumber);
  tierConditionAmount.setSpentAmount(money);

  const request = new UpgradeCustomerTierRequest();
  request.setId(customerId);
  request.setTierConditionType(tierConditionType);
  request.setAmount(tierConditionAmount);
  try {
    const response: UpgradeCustomerTierResponse = await customerClient.upgradeCustomerTier(request);
    return response.toObject();
  } catch (e) {
    console.error(`Error on upgrade customer tier level from customer [${customerId}]`, e);
    throw new Error("Unexpected reply upgrading customer tier level");
  }
}

function getUnitAndCents(amount: number): [number, number] {
  const [units, cents] = String(amount).split(".");
  return [+units, cents ? +cents : 0];
}

function amountAsMoney(amount: TierLevelUpgradeTransactionAmount): Money {
  const [units, cents] = getUnitAndCents(amount.spentAmount.amount);
  const money = new Money();
  money.setCurrencyCode(amount.spentAmount.currency);
  money.setUnits(units);
  money.setNanos(cents * 10000000);
  return money;
}
